import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const comic = getComicById(Number(id));
  const relatedComics = comic ? getRelatedComics(comic) : [];

  if (!comic) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-search-line text-4xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-medium px-5 py-2.5 rounded-full text-sm whitespace-nowrap transition-colors duration-200"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-15',
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩漫之家',
      url: 'https://yangyijixie.cn',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://yangyijixie.cn/article/${comic.id}`,
    },
  };

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  return (
    <div className="min-h-screen pt-16">
      <div className="bg-background-100/70 py-6">
        <div className="w-full max-w-[1280px] mx-auto">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
          {/* Cover */}
          <div className="shrink-0 w-full sm:w-[280px] mx-auto lg:mx-0">
            <div className="aspect-[2/3] rounded-lg overflow-hidden">
              <img
                src={comic.cover}
                alt={comic.title}
                title={`${comic.title} - 免费韩漫大全_韩国漫画在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex gap-3">
              <Link
                to={`/category/${comic.categorySlug}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-secondary-100 text-secondary-700 hover:bg-secondary-200 font-medium px-4 py-2.5 rounded-full text-sm whitespace-nowrap transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i> {t('article.backToList')}
              </Link>
              <button
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white font-medium px-4 py-2.5 rounded-full text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer"
              >
                {t('article.startReading')} <i className="ri-play-line"></i>
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
              {comic.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-5">
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-user-line text-base"></i>
                <strong>{t('article.author')}：</strong>{comic.author}
              </span>
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-flag-line text-base"></i>
                <strong>{t('article.status')}：</strong>
                <span className={comic.status === '连载中' ? 'text-primary-500' : 'text-foreground-500'}>
                  {comic.status === '连载中' ? t('article.ongoing') : t('article.completed')}
                </span>
              </span>
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-file-list-3-line text-base"></i>
                <strong>{t('article.chapters')}：</strong>{comic.chapters}话
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-5">
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-calendar-line text-base"></i>
                <strong>{t('article.updateDate')}：</strong>{comic.updateDate}
              </span>
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-star-fill text-accent-400 text-base"></i>
                <strong>{t('article.rating')}：</strong>
                <span className="font-bold text-accent-500">{comic.rating}</span>
              </span>
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-eye-line text-base"></i>
                <strong>{t('article.views')}：</strong>
                {comic.views.toLocaleString()}
              </span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="text-xs text-foreground-500">{t('article.tags')}：</span>
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium border border-secondary-200"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-foreground-900 mb-2">
                {t('article.description')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {comic.description}
              </p>
            </div>

            {/* Latest Chapter */}
            <div className="bg-background-100/70 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">{t('article.latestChapter')}</p>
                  <p className="text-sm font-semibold text-foreground-900">{comic.latestChapter}</p>
                </div>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="inline-flex items-center gap-1 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-full whitespace-nowrap transition-colors duration-200"
                >
                  <i className="ri-play-circle-line"></i> {t('article.startReading')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Chapter List */}
        <div className="mt-10">
          <h3 className="text-lg font-heading text-foreground-900 mb-4">
            {t('article.chapterList')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => (
              <div
                key={i}
                className="px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
              >
                <span className="text-sm text-foreground-700 block truncate">
                  第{comic.chapters - i}话
                </span>
              </div>
            ))}
            {comic.chapters > 20 && (
              <div className="px-3 py-2.5 rounded-lg border border-dashed border-background-300 bg-background-50 flex items-center justify-center cursor-pointer hover:border-primary-300 transition-colors duration-200">
                <span className="text-sm text-foreground-400">
                  查看更多章节...
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <div className="mt-12">
            <h3 className="text-lg font-heading text-foreground-900 mb-5">
              {t('article.relatedComics')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {relatedComics.map((rc) => (
                <ComicCard key={`related-${rc.id}`} comic={rc} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '韩漫之家', item: 'https://yangyijixie.cn/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: `https://yangyijixie.cn/category/${comic.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          }),
        }}
      />
    </div>
  );
}